import type {
  EdgeEntityDataAttributes,
  ProtocolEdgeType,
} from '@ipfabric/utils';

/**
 * Names of columns containing given value for filtering
 */
type EdgeEntityTableFilterColumns = {
  [ValueType in keyof EdgeEntityDataAttributes]: string;
};

export interface ProtocolTableFilterColumns {
  source?: EdgeEntityTableFilterColumns;
  target?: EdgeEntityTableFilterColumns;
  vlanId?: string;
}

export type TableFiltersAttributes = {
  [webendpoint: string]: ProtocolTableFilterColumns;
};

export type TablesInProtocols = {
  [Protocol in ProtocolEdgeType]?: string[];
};

export const MTU = '/technology/interfaces/mtu';
export const CDP_LLDP_ALL_NEIGHBORS = '/technology/cdp-lldp/all-neighbors';
export const ROUTES = '/technology/routing/routes';
export const ROUTES_IPV6 = '/technology/routing/routes/ipv6';
export const MANAGED_IP = '/technology/addressing/managed-ip/ipv4';
export const MANAGED_IP_IPV6 = '/technology/addressing/managed-ip/ipv6';
export const INTERFACES = '/inventory/interfaces';
export const ARP_TABLE = '/technology/addressing/arp-table';
export const BGP_NEIGHBORS = '/technology/routing/bgp/neighbors';
export const ADDRESS_FAMILIES = '/technology/routing/bgp/address-families';
export const OSPF_NEIGHBORS = '/technology/routing/ospf/neighbors';
export const OSPF_INTERFACES = '/technology/routing/ospf/interfaces';
export const OSPFV3_NEIGHBORS = '/technology/routing/ospf-v3/neighbors';
export const OSPFV3_INTERFACES = '/technology/routing/ospf-v3/interfaces';
export const SWITCHPORT = '/technology/interfaces/switchport';
export const VIRTUAL_PORTS = '/technology/spanning-tree/stp-virtual-ports';
export const EIGRP_NEIGHBORS = '/technology/routing/eigrp/neighbors';
export const EIGRP_INTERFACES = '/technology/routing/eigrp/interfaces';
export const LDP_NEIGHBORS = '/technology/mpls/ldp/neighbors';
export const LDP_INTERFACES = '/technology/mpls/ldp/interfaces';
export const RIP_NEIGHBORS = '/technology/routing/rip/neighbors';
export const RIP_INTERFACES = '/technology/routing/rip/interfaces';
export const PIM_NEIGHBORS = '/technology/multicast/pim/neighbors';
export const PIM_INTERFACES = '/technology/multicast/pim/interfaces';
export const ISIS_NEIGHBORS = '/technology/routing/is-is/neighbors';
export const ISIS_INTERFACES = '/technology/routing/is-is/interfaces';
export const STP_NEIGHBORS = '/technology/spanning-tree/stp-neighbors';
export const VXLAN_PEERS = '/technology/sdn/vxlan/peers';
export const INVENTORY_HOSTS = '/inventory/hosts';
export const WIRELESS_ACCESS_POINTS = '/technology/wireless/access-points';
export const WIRELESS_CLIENTS = '/technology/wireless/clients';
export const FEX_MODULES = '/technology/platforms/fex/fex-modules';
export const FEX_INTERFACES = '/technology/platforms/fex/fex-ints';
export const RSVP_NEIGHBORS = '/technology/mpls/rsvp/neighbors';
export const RSVP_INTERFACES = '/technology/mpls/rsvp/interfaces';
export const CLOUD_NETWORK_PEERINGS = '/technology/cloud/networks/peerings';

export const tablesInProtocols: TablesInProtocols = {
  dgw: [ROUTES, MANAGED_IP, INTERFACES, ARP_TABLE, MTU], // Same as RIB
  ebgp: [BGP_NEIGHBORS, ADDRESS_FAMILIES],
  eigrp: [EIGRP_NEIGHBORS, EIGRP_INTERFACES, MTU],
  ibgp: [BGP_NEIGHBORS, ADDRESS_FAMILIES],
  isis: [ISIS_NEIGHBORS, ISIS_INTERFACES, MTU],
  ldp: [LDP_NEIGHBORS, LDP_INTERFACES, MTU],
  ospf: [OSPF_NEIGHBORS, OSPF_INTERFACES, MTU],
  ospfv3: [OSPFV3_NEIGHBORS, OSPFV3_INTERFACES, MTU],
  pim: [PIM_NEIGHBORS, PIM_INTERFACES, MTU],
  rib: [ROUTES, MANAGED_IP, INTERFACES, ARP_TABLE, MTU],
  rip: [RIP_NEIGHBORS, RIP_INTERFACES, MTU],
  stp: [SWITCHPORT, VIRTUAL_PORTS, MTU],
  stpi: [STP_NEIGHBORS, VIRTUAL_PORTS],
  'stpi-blocked': [STP_NEIGHBORS, VIRTUAL_PORTS],
  vxlan: [VXLAN_PEERS],
  xdp: [CDP_LLDP_ALL_NEIGHBORS, MTU],
  wired: [INVENTORY_HOSTS],
  wireless: [WIRELESS_ACCESS_POINTS, WIRELESS_CLIENTS],
  fex: [FEX_MODULES, FEX_INTERFACES],
  rsvp: [RSVP_NEIGHBORS, RSVP_INTERFACES],
  ribIpv6: [ROUTES_IPV6, MANAGED_IP_IPV6, MTU], // Add remaining IPv6 tables from RIB when they're implemented
  dgwIpv6: [ROUTES_IPV6, MANAGED_IP_IPV6, MTU], // Add remaining IPv6 tables from DGW when they're implemented
  'cloud-peering': [CLOUD_NETWORK_PEERINGS],
};

export const tableFiltersAttributes: TableFiltersAttributes = {
  [CDP_LLDP_ALL_NEIGHBORS]: {
    source: {
      sn: 'localSn',
      intName: 'localInt',
    },
    target: {
      sn: 'remoteSn',
      intName: 'remoteInt',
    },
  },
  [MTU]: {
    source: {
      sn: 'srcSn',
      intName: 'srcInt',
    },
    target: {
      sn: 'dstSn',
      intName: 'dstInt',
    },
  },
  [ROUTES]: {
    source: {
      sn: 'sn',
      intName: 'nexthop',
    },
  },
  [ROUTES_IPV6]: {
    source: {
      sn: 'sn',
      intName: 'nexthop',
    },
  },
  [MANAGED_IP]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [MANAGED_IP_IPV6]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [ARP_TABLE]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [BGP_NEIGHBORS]: {
    source: {
      sn: 'sn',
      ipAddress: 'localAddress',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [ADDRESS_FAMILIES]: {
    source: {
      sn: 'sn',
      ipAddress: 'localAddress',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [OSPF_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [OSPF_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [OSPFV3_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      ipAddress: 'neiAddress',
    },
  },
  [OSPFV3_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [SWITCHPORT]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [VIRTUAL_PORTS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'sn',
      intName: 'intName',
    },
    vlanId: 'vlanId',
  },
  [EIGRP_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [EIGRP_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [LDP_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [LDP_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [RIP_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [RIP_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [PIM_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [PIM_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [ISIS_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [ISIS_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
  [VXLAN_PEERS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
    },
  },
  [STP_NEIGHBORS]: {
    source: {
      sn: 'sn',
    },
    target: {
      sn: 'snDst',
    },
    vlanId: 'vlanId',
  },
  [INVENTORY_HOSTS]: {
    target: {
      sn: 'uniqId',
      intName: 'intName',
    },
  },
  [WIRELESS_ACCESS_POINTS]: {
    source: {
      sn: 'controllerSn',
    },
    target: {
      sn: 'apSn',
    },
  },
  [WIRELESS_CLIENTS]: {
    source: {
      sn: 'controllerSn',
    },
    target: {
      sn: 'apSn',
    },
  },
  [FEX_MODULES]: {
    target: {
      sn: 'sn',
    },
  },
  [FEX_INTERFACES]: {
    target: {
      sn: 'fexSn',
      intName: 'fexIntName',
    },
  },
  [RSVP_NEIGHBORS]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
    target: {
      sn: 'neiSn',
      ipAddress: 'neiAddress',
    },
  },
  [RSVP_INTERFACES]: {
    source: {
      sn: 'sn',
      intName: 'intName',
    },
  },
};
